// Reporting's Download dialog (owner, 2026-11-03): PDF or Excel, and whether to
// include the invoices' own documents. `lib/chartExport.ts` says what each
// combination produces; this only asks.
import { useEffect, useState } from 'react'
import {
  Body1,
  Button,
  Caption1,
  Checkbox,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  ProgressBar,
  Radio,
  RadioGroup,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { AppDialog } from './AppDialog'
import { FrozenWhileClosed } from './FrozenWhileClosed'
import { useDialogFormStyles } from './dialogForm'
import { ErrorBanner } from './States'
import type { ExportFormat } from '../lib/chartExport'

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  hint: { color: tokens.colorNeutralForeground3, display: 'block', marginInlineStart: '32px' },
})

export interface ExportChoice {
  format: ExportFormat
  includeInvoices: boolean
}

interface Props {
  open: boolean
  chartTitle: string
  /** The listing's count, for the sentence under the checkbox. `null` while unknown. */
  invoiceCount: number | null
  /** The most invoices a report with documents may cover. */
  invoiceCap: number
  busy: boolean
  /** What the export is doing now, while `busy`. */
  progressLabel: string | null
  progress: number | null
  error: string | null
  onDownload: (choice: ExportChoice) => void
  onClose: () => void
}

export function ReportExportDialog({
  open,
  chartTitle,
  invoiceCount,
  invoiceCap,
  busy,
  progressLabel,
  progress,
  error,
  onDownload,
  onClose,
}: Props) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const [format, setFormat] = useState<ExportFormat>('pdf')
  const [includeInvoices, setIncludeInvoices] = useState(false)
  // Each opening starts from the plain PDF, the most common ask.
  useEffect(() => {
    if (open) {
      setFormat('pdf')
      setIncludeInvoices(false)
    }
  }, [open])

  const overCap = invoiceCount !== null && invoiceCount > invoiceCap
  const invoicesHint = overCap
    ? `Unavailable: these filters match ${invoiceCount} invoices, and documents can be included for at most ${invoiceCap}. Narrow the filters to include them.`
    : format === 'pdf'
      ? "Each invoice's own pages are added after the chart and listing, in the same file."
      : "A spreadsheet cannot hold PDF pages, so the invoices' pages download as a second file, a PDF."

  return (
    <AppDialog open={open} onOpenChange={(_, d) => !d.open && !busy && onClose()}>
      <DialogSurface>
        <FrozenWhileClosed open={open}>
          <DialogBody>
            <DialogTitle>Download {chartTitle}</DialogTitle>
            <DialogContent className={f.beforeActions}>
              <div className={s.body}>
                <Field label="Format">
                  <RadioGroup value={format} onChange={(_, d) => setFormat(d.value as ExportFormat)} disabled={busy}>
                    <Radio value="pdf" label="PDF: the chart as drawn, then the invoice listing" />
                    <Radio value="excel" label="Excel: the chart's figures and the invoice listing, one sheet each" />
                  </RadioGroup>
                </Field>
                <div>
                  <Checkbox
                    checked={includeInvoices && !overCap}
                    disabled={busy || overCap}
                    onChange={(_, d) => setIncludeInvoices(d.checked === true)}
                    label="Include the invoices' PDFs"
                  />
                  <Caption1 className={s.hint}>{invoicesHint}</Caption1>
                </div>
                {busy && progressLabel !== null && (
                  <div>
                    <Body1>{progressLabel}</Body1>
                    {progress !== null && <ProgressBar value={progress} thickness="large" />}
                  </div>
                )}
                <ErrorBanner message={error} />
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={busy}
                onClick={() => onDownload({ format, includeInvoices: includeInvoices && !overCap })}
              >
                {busy ? 'Downloading…' : 'Download'}
              </Button>
            </DialogActions>
          </DialogBody>
        </FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}
